const AFFILIATE_REFERRAL_STORAGE_KEY = "affiliate_referral_code";
const AFFILIATE_REFERRAL_TTL_MS = 20 * 60 * 1000;
const LEGACY_QUERY_KEYS = ["referral_code", "referral", "ref", "code"];

const normalizeReferralCode = (value) => String(value ?? "").trim();

const getSearch = () => {
  if (typeof window === "undefined") return "";
  return window.location.search;
};

const getStorage = (name) => {
  if (typeof window === "undefined") return null;

  try {
    return window[name];
  } catch (error) {
    return null;
  }
};

const getQueryCode = (search, key) => {
  try {
    return normalizeReferralCode(new URLSearchParams(search).get(key));
  } catch (error) {
    return "";
  }
};

const removeStoredCode = (storage) => {
  try {
    storage?.removeItem(AFFILIATE_REFERRAL_STORAGE_KEY);
  } catch (error) {
    // Storage can be blocked by the browser. The form should remain usable.
  }
};

const readStoredCode = (storage, now) => {
  if (!storage) return "";

  try {
    const rawValue = storage.getItem(AFFILIATE_REFERRAL_STORAGE_KEY);
    if (!rawValue) return "";

    const normalizedRawValue = normalizeReferralCode(rawValue);
    const looksStructured = normalizedRawValue.startsWith("{") || normalizedRawValue.startsWith("[");
    const isJsonString = normalizedRawValue.startsWith('"');

    if (!looksStructured && !isJsonString) return normalizedRawValue;

    try {
      const parsed = JSON.parse(rawValue);
      if (typeof parsed === "string") return normalizeReferralCode(parsed);
      if (!parsed || typeof parsed !== "object") return "";

      const expiresAt = Number(parsed.expiresAt);
      if (!Number.isFinite(expiresAt) || now > expiresAt) {
        removeStoredCode(storage);
        return "";
      }

      return normalizeReferralCode(parsed.code);
    } catch (error) {
      if (looksStructured) {
        removeStoredCode(storage);
        return "";
      }

      return normalizedRawValue;
    }
  } catch (error) {
    return "";
  }
};

export const captureAffiliateReferralCode = (search = getSearch(), now = Date.now()) => {
  const code = getQueryCode(search, "affiliate");
  if (!code) return "";

  const storage = getStorage("sessionStorage");
  try {
    storage?.setItem(
      AFFILIATE_REFERRAL_STORAGE_KEY,
      JSON.stringify({ code, expiresAt: now + AFFILIATE_REFERRAL_TTL_MS })
    );
  } catch (error) {
    // The URL value still works even when session storage is unavailable.
  }

  return code;
};

export const getInitialReferralCodeState = (search = getSearch(), now = Date.now()) => {
  const affiliateCode = captureAffiliateReferralCode(search, now);
  if (affiliateCode) return { code: affiliateCode, locked: true };

  for (const key of LEGACY_QUERY_KEYS) {
    const legacyCode = getQueryCode(search, key);
    if (legacyCode) return { code: legacyCode, locked: false };
  }

  const sessionCode = readStoredCode(getStorage("sessionStorage"), now);
  if (sessionCode) return { code: sessionCode, locked: true };

  const localCode = readStoredCode(getStorage("localStorage"), now);
  if (localCode) return { code: localCode, locked: true };

  return { code: "", locked: false };
};
